'use client';
import { importLibrary as googleImportLibrary, setOptions } from '@googlemaps/js-api-loader';
import type { Market } from '@//config/domain-locale-map';

interface MarketCache {
  libraryCache: Map<string, any>;
  loadingPromises: Map<string, Promise<any>>;
  optionsSet: boolean;
}

const marketCache = new Map<Market, MarketCache>();

const setMarketOptions = (market: Market): void => {
  const apiKey =
    market === 'drive'
      ? process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY_DRIVE
      : process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY_WAYKE;

  if (!apiKey) {
    throw new Error(
      `Missing required NEXT_PUBLIC_GOOGLE_MAPS_API_KEY_${market.toUpperCase()} environment variable. Please define it.`,
    );
  }

  setOptions({
    key: apiKey,
    language: market === 'drive' ? 'nb' : 'sv',
    region: market === 'drive' ? 'NO' : 'SE',
  });
};

const getMarketCache = (market: Market): MarketCache => {
  if (!marketCache.has(market)) {
    marketCache.set(market, {
      libraryCache: new Map(),
      loadingPromises: new Map(),
      optionsSet: false,
    });
  }
  return marketCache.get(market)!;
};

export const googleMapsLoader = {
  async importLibrary<T = any>(market: Market, libraryName: string): Promise<T> {
    const cache = getMarketCache(market);

    // Set options only once per market
    if (!cache.optionsSet) {
      setMarketOptions(market);
      cache.optionsSet = true;
    }

    if (cache.libraryCache.has(libraryName)) {
      return cache.libraryCache.get(libraryName);
    }

    if (cache.loadingPromises.has(libraryName)) {
      return cache.loadingPromises.get(libraryName);
    }

    const loadingPromise = googleImportLibrary(libraryName).then((library) => {
      cache.libraryCache.set(libraryName, library);
      cache.loadingPromises.delete(libraryName);
      return library as T;
    });

    cache.loadingPromises.set(libraryName, loadingPromise);
    return loadingPromise;
  },

  clearCache(market?: Market): void {
    if (market) {
      marketCache.delete(market);
    } else {
      marketCache.clear();
    }
  },
};

export default googleMapsLoader.importLibrary;
